import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { hydraApi, apiClient } from '../api/client'
import { ErrorMessage } from '../components/ErrorMessage'
import { AuthLayout, AuthLoading } from '../components/AuthLayout'
import { browserRedirectUrl } from '../api/flows'
import { useI18n } from '../i18n'

interface ConsentRequest {
  challenge: string
  requested_scope: string[]
  requested_access_token_audience: string[]
  skip: boolean
  subject: string
  oidc_context?: {
    display_name?: string
  }
  client?: {
    client_id: string
    client_name: string
    logo_uri?: string
    policy_uri?: string
    tos_uri?: string
  }
}

export default function ConsentPage() {
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const challenge = searchParams.get('consent_challenge')

  const [consentReq, setConsentReq] = useState<ConsentRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [retry, setRetry] = useState(0)
  const lifecycle = useRef<{ active: boolean } | null>(null)
  const submissionLocked = useRef(false)

  useEffect(() => {
    const scope = { active: true }
    lifecycle.current = scope
    submissionLocked.current = false
    setSubmitting(false)
    setLoading(true)
    setError(null)
    setConsentReq(null)
    if (!challenge) {
      setError('Missing consent challenge')
      setLoading(false)
      return
    }

    const fetchConsentRequest = async () => {
      try {
        const res = await apiClient.get<ConsentRequest>('/auth/hydra/consent-info', { params: { consent_challenge: challenge } })
        if (!scope.active) return
        setConsentReq(res.data)
        setSelectedScopes(res.data.requested_scope || [])
      } catch (err) {
        if (scope.active) setError('Failed to load consent request. Please try again.')
      } finally {
        if (scope.active) setLoading(false)
      }
    }

    fetchConsentRequest()
    return () => { scope.active = false }
  }, [challenge, retry])

  const handleDecision = async (allow: boolean) => {
    const scope = lifecycle.current
    if (!challenge || !consentReq || submissionLocked.current || !scope?.active) return
    submissionLocked.current = true
    setSubmitting(true)
    setError(null)

    try {
      const res = await hydraApi.acceptConsent(challenge, allow ? selectedScopes : [], allow, allow)
      // Navigation is asynchronous; keep the form locked until the page leaves.
      if (scope.active) window.location.assign(browserRedirectUrl(res.data.redirect_to, window.location.origin))
    } catch (err) {
      if (scope.active) {
        setError(allow ? 'Failed to accept consent. Please try again.' : 'Failed to reject consent. Please try again.')
        submissionLocked.current = false
        setSubmitting(false)
      }
    }
  }

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    )
  }

  const getScopeDescription = (scope: string): string => {
    const descriptions: Record<string, string> = {
      openid: 'Authenticate you using OpenID Connect',
      profile: 'Access your basic profile information',
      email: 'Access your email address',
      offline_access: 'Access your account even when you are offline',
    }
    return Object.prototype.hasOwnProperty.call(descriptions, scope)
      ? t(descriptions[scope])
      : t('Access scope: {scope}', { scope })
  }

  if (loading) {
    return (
      <AuthLayout title={t('Authorization Request')}>
        <AuthLoading message={t('Loading consent request...')} />
      </AuthLayout>
    )
  }

  if (error && !consentReq) {
    return (
      <AuthLayout title={t('Authorization Request')}>
        <ErrorMessage message={t(error)} />
        {challenge && <button onClick={() => setRetry(value => value + 1)} className="mt-4 w-full btn-primary">
          {t('Try again')}
        </button>}
        <button onClick={() => navigate('/')} className="mt-3 w-full btn-secondary">
          {t('Go Home')}
        </button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('Authorization Request')}
      description={t('{application} is requesting access to your account', { application: consentReq?.client?.client_name || t('An application') })}
    >
      {error && <ErrorMessage message={t(error)} className="mb-4" />}
      {consentReq?.client?.logo_uri && (
        <div className="flex justify-center mb-6">
          <img
            src={consentReq.client.logo_uri}
            alt={t('{application} logo', { application: consentReq.client.client_name || t('An application') })}
            width="64"
            height="64"
            className="h-16 w-16 object-contain"
          />
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-sm font-medium text-gray-900 mb-3">
          {t('This application will be able to:')}
        </h2>
        <div className="space-y-3">
          {consentReq?.requested_scope?.map((scope) => (
            <div key={scope} className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id={`scope-${scope}`}
                  type="checkbox"
                  checked={selectedScopes.includes(scope)}
                  disabled={submitting}
                  onChange={() => toggleScope(scope)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm min-w-0">
                <label htmlFor={`scope-${scope}`} className="font-medium text-gray-700 break-words">
                  {scope}
                </label>
                <p className="text-gray-500 break-words">{getScopeDescription(scope)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={() => handleDecision(true)}
          disabled={submitting || selectedScopes.length === 0}
          className="w-full btn-primary"
        >
          {submitting ? t('Processing...') : t('Allow Access')}
        </button>
        <button
          onClick={() => handleDecision(false)}
          disabled={submitting}
          className="w-full btn-secondary"
        >
          {t('Deny')}
        </button>
      </div>

      <div className="mt-6 text-center text-xs text-gray-500">
        <p>
          {t('Allow access only if you trust this application.')}
        </p>
        {consentReq?.client?.tos_uri && (
          <p className="mt-2">
            {t('By allowing access, you agree to the')}{' '}
            <a href={consentReq.client.tos_uri} className="auth-link" target="_blank" rel="noopener noreferrer">
              {t('Terms of Service')}
            </a>
          </p>
        )}
      </div>
    </AuthLayout>
  )
}
